<template>
  <b-modal
      id="cptu-import-modal"
      ref="cptuImportModal"
      v-model="isModalOpened"
      title="Importer un fichier CPTu"
      centered
      ok-title="Importer"
      cancel-title="Annuler"
      @ok.prevent="handleUpload"
      @hidden="resetForm"
      :ok-disabled="!selectedFile || isUploading"
  >
    <b-form>
      <b-form-group
          label="Sélectionner un fichier CPTu"
          description="Formats supportés: .csv, .xls, .xlsx"
      >
        <b-form-file
            v-model="selectedFile"
            :state="fileState"
            accept=".csv, .xls, .xlsx"
            placeholder="Choisir un fichier ou le glisser ici..."
            drop-placeholder="Déposer le fichier ici..."
            :disabled="isUploading"
            @change="validateFile"
        ></b-form-file>
        <div class="mt-1 text-danger" v-if="fileError">
          {{ fileError }}
        </div>
      </b-form-group>

      <!-- Progress bar for upload -->
      <b-progress
          v-if="uploadProgress > 0"
          :value="uploadProgress"
          :max="100"
          show-value
          class="mt-3"
      ></b-progress>
    </b-form>
  </b-modal>
</template>

<script>
import {BForm, BFormFile, BFormGroup, BModal, BProgress} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: 'CPTuImportModal',
  components: {
    BModal,
    BProgress,
    BFormFile,
    BFormGroup,
    BForm
  },
  props: ["isOpened"],
  data() {
    return {
      selectedFile: null,
      fileError: null,
      isUploading: false,
      uploadProgress: 0,
      chunkSize: 1024 * 1024, // 1MB chunks
    }
  },
  computed: {
    fileState() {
      return this.selectedFile ? !this.fileError : null
    },
    isModalOpened: {
      get() {
        return this.isOpened;
      },
      set(value) {
        this.$emit("update:isOpened", value);
      },
    },
  },
  methods: {
    validateFile() {
      this.fileError = null

      if (!this.selectedFile) return

      // Validate file type
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]

      if (!allowedTypes.includes(this.selectedFile.type)) {
        this.fileError = 'Format de fichier non supporté. Veuillez utiliser CSV, XLS ou XLSX.'
        this.selectedFile = null
        return
      }

      // Validate file size if needed
      if (this.selectedFile.size > 100 * 1024 * 1024) { // 100MB limit
        this.fileError = 'Le fichier est trop volumineux. Taille maximale: 100MB.'
        this.selectedFile = null
      }
    },

    async handleUpload(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.selectedFile || this.fileError) return

      this.isUploading = true
      this.uploadProgress = 0

      try {
        await this.uploadFileInChunks()
        this.$emit('upload-complete')
        this.isModalOpened = false
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Erreur lors du téléchargement: ${error.message || 'Veuillez réessayer'}`,
            icon: "AlertTriangleIcon",
            variant: "error",
          }})
      } finally {
        this.isUploading = false
      }
    },

    async uploadFileInChunks() {
      const file = this.selectedFile
      const totalChunks = Math.ceil(file.size / this.chunkSize)
      let uploadedChunks = 0

      // Generate a unique ID for this upload session
      const uploadId = Date.now().toString()

      for (let start = 0; start < file.size; start += this.chunkSize) {
        const chunk = file.slice(start, start + this.chunkSize)
        const formData = new FormData()

        formData.append('file', chunk)
        formData.append('file_name', file.name)
        formData.append('upload_id', uploadId)
        formData.append('chunk_index', Math.floor(start / this.chunkSize))
        formData.append('total_chunks', totalChunks)

        await store.dispatch('cptu/uploadFileChunk', formData)

        uploadedChunks++
        this.uploadProgress = Math.floor((uploadedChunks / totalChunks) * 100)
      }

      // Notify the server that all chunks have been uploaded
      await store.dispatch('cptu/finalizeUpload', {
        upload_id: uploadId,
        file_name: file.name,
        file_type: file.type,
        total_chunks: totalChunks,
      })

      return true
    },

    resetForm() {
      this.selectedFile = null
      this.fileError = null
      this.isUploading = false
      this.uploadProgress = 0
    },
  }
}
</script>